import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import { acceleratedRaycast } from "three-mesh-bvh";
import { GenerateMeshBVHWorker } from "three-mesh-bvh/src/workers/GenerateMeshBVHWorker.js";
import { Visualisation } from "./visualisation";
import { Colors } from "../colors";
import {
  ColorBufferMaterial,
  ScanBodySelectMaterial as ScanBodySelectMaterial,
} from "../materials";

// Add the extension functions for three-mesh-bvh.
THREE.Mesh.prototype.raycast = acceleratedRaycast;

const dragThreshold = 3;

export class ScanBodySelect extends Visualisation {
  static make(element, data) {
    const geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(data.mesh.vertexPositions);
    const normals = new Float32Array(data.mesh.vertexNormals);
    const indices = new Uint32Array(data.mesh.indices);
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    const worker = new GenerateMeshBVHWorker();
    return worker
      .generate(geometry, { maxLeafTris: 5, indirect: true })
      .then((bvh) => {
        geometry.boundsTree = bvh;
        return new ScanBodySelect(
          element,
          geometry,
          data.detectedPlanesForScanGauges,
          data.detectedPlanesForNarrowScanBodies
        );
      });
  }

  constructor(
    element,
    geometry,
    detectedPlanesForScanGauges,
    detectedPlanesForNarrowScanBodies
  ) {
    super(element);

    this.selectingNarrowScanBodies = false;
    this.dragging = false;
    this.startDragPos = new THREE.Vector2();
    this.endDragPos = new THREE.Vector2();
    this.mouseCoordsNDC = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();
    this.raycaster.firstHitOnly = true;

    this.geometry = geometry;
    this.scanBodySelectMaterial = new ScanBodySelectMaterial();
    this.colorsMaterial = new ColorBufferMaterial();

    const position = this.geometry.getAttribute("position");
    const index = this.geometry.getIndex();
    const indices = index.array;

    this.scanGaugePlaneFaceIndices = [];
    this.scanGaugeFaceIndexToPlaneIndex = new Int32Array(index.count).fill(-1);
    this.narrowScanBodyPlaneFaceIndices = [];
    this.narrowScanBodyFaceIndexToPlaneIndex = new Int32Array(index.count).fill(
      -1
    );

    {
      const planeIndices = new Int32Array(position.array.length).fill(-1);

      for (const detectedPlane of detectedPlanesForScanGauges) {
        this.scanGaugePlaneFaceIndices.push(
          new Uint32Array(detectedPlane.faceIndices)
        );
      }
      for (let i = 0; i < detectedPlanesForScanGauges.length; ++i) {
        const faceIndices = this.scanGaugePlaneFaceIndices[i];
        for (const faceIndex of faceIndices) {
          planeIndices[indices[faceIndex * 3]] = i;
          planeIndices[indices[faceIndex * 3 + 1]] = i;
          planeIndices[indices[faceIndex * 3 + 2]] = i;
          this.scanGaugeFaceIndexToPlaneIndex[faceIndex] = i;
        }
      }
      this.geometry.setAttribute(
        "scanGaugePlaneIndex",
        new THREE.BufferAttribute(planeIndices, 1)
      );
    }

    {
      const planeIndices = new Int32Array(position.array.length).fill(-1);

      if (detectedPlanesForNarrowScanBodies) {
        for (const detectedPlane of detectedPlanesForNarrowScanBodies) {
          this.narrowScanBodyPlaneFaceIndices.push(
            new Uint32Array(detectedPlane.faceIndices)
          );
        }
        for (let i = 0; i < detectedPlanesForNarrowScanBodies.length; ++i) {
          const faceIndices = this.narrowScanBodyPlaneFaceIndices[i];
          for (const faceIndex of faceIndices) {
            planeIndices[indices[faceIndex * 3]] = i;
            planeIndices[indices[faceIndex * 3 + 1]] = i;
            planeIndices[indices[faceIndex * 3 + 2]] = i;
            this.narrowScanBodyFaceIndexToPlaneIndex[faceIndex] = i;
          }
        }
      }
      this.geometry.setAttribute(
        "narrowScanBodyPlaneIndex",
        new THREE.BufferAttribute(planeIndices, 1)
      );
    }

    const colors1 = new THREE.BufferAttribute(
      new Float32Array(position.array.length),
      3
    );
    const colors2 = new THREE.BufferAttribute(
      new Float32Array(position.array.length),
      3
    );
    this.geometry.setAttribute("color1", colors1);
    this.geometry.setAttribute("color2", colors2);

    this.mesh = new THREE.Mesh(this.geometry, this.scanBodySelectMaterial);
    this.highlightedPlaneIndex = -1;
    this.narrowScanBodyHighlightedPlaneIndex = -1;
    this.selectedPlaneIndicesForScanGauges = [];
    this.selectedPlaneIndicesForNarrowScanBodies = [];
    this.scene.add(this.mesh);

    this.reset();
  }

  dispose() {
    super.dispose();

    this.geometry.dispose();
    this.scanBodySelectMaterial.dispose();
    this.colorsMaterial.dispose();
  }

  reset() {
    this.selectedPlaneIndicesForScanGauges = [];
    this.selectedPlaneIndicesForNarrowScanBodies = [];

    const uniforms = this.scanBodySelectMaterial.uniforms;
    uniforms.scanGaugeSelectedPlaneIndices.value = new Array(32).fill(-1);
    uniforms.scanGaugeSelectedPlanesCount.value = 0;
    uniforms.narrowScanBodySelectedPlaneIndices.value = new Array(32).fill(-1);
    uniforms.narrowScanBodySelectedPlanesCount.value = 0;

    this.startSelectionForScanGauges();

    const colors1 = this.geometry.getAttribute("color1");
    const colors2 = this.geometry.getAttribute("color2");
    Visualisation.fillColorBufferAttribute(
      colors1,
      new THREE.Color(Colors.gray)
    );
    Visualisation.fillColorBufferAttribute(
      colors2,
      new THREE.Color(Colors.gray)
    );

    colors1.needsUpdate = true;
    colors2.needsUpdate = true;
    this.mesh.material = this.scanBodySelectMaterial;
  }

  startSelectionForScanGauges() {
    this.enableSelection = true;
    this.selectingNarrowScanBodies = false;

    this.selectedPlaneIndicesForScanGauges = [];

    this.clearHighlight();

    const uniforms = this.scanBodySelectMaterial.uniforms;
    uniforms.selectingNarrowScanBodies.value = 0;
    uniforms.scanGaugeSelectedPlaneIndices.value = new Array(32).fill(-1);
    uniforms.scanGaugeSelectedPlanesCount.value = 0;
  }

  startSelectionForNarrowScanBodies() {
    this.enableSelection = true;
    this.selectingNarrowScanBodies = true;

    this.selectedPlaneIndicesForNarrowScanBodies = [];

    this.clearHighlight();

    const uniforms = this.scanBodySelectMaterial.uniforms;
    uniforms.selectingNarrowScanBodies.value = 1;
    uniforms.narrowScanBodySelectedPlaneIndices.value = new Array(32).fill(-1);
    uniforms.narrowScanBodySelectedPlanesCount.value = 0;
  }

  stopSelection() {
    this.enableSelection = false;
  }

  clearSelections() {
    if (!this.enableSelection) {
      return;
    }

    const uniforms = this.scanBodySelectMaterial.uniforms;
    let uniformSelectedPlaneIndices = uniforms.scanGaugeSelectedPlaneIndices;
    let uniformSelectedPlanesCount = uniforms.scanGaugeSelectedPlanesCount;
    if (this.selectingNarrowScanBodies) {
      this.selectedPlaneIndicesForNarrowScanBodies = [];
      uniformSelectedPlaneIndices = uniforms.narrowScanBodySelectedPlaneIndices;
      uniformSelectedPlanesCount = uniforms.narrowScanBodySelectedPlanesCount;
    } else {
      this.selectedPlaneIndicesForScanGauges = [];
    }

    uniformSelectedPlaneIndices.value = new Array(32).fill(-1);
    uniformSelectedPlanesCount.value = 0;
  }

  showSelectedScanBodies(gauges, narrowScanBodies) {
    const indices = this.geometry.getIndex();
    const colors1 = this.geometry.getAttribute("color1");
    const colors2 = this.geometry.getAttribute("color2");
    const scanGaugeColor = new THREE.Color(Colors.primary);
    const narrowScanBodyColor = new THREE.Color(Colors.narrowScanBodySelected);

    for (const selectedPlaneIndex of this.selectedPlaneIndicesForScanGauges) {
      const faceIndices = this.scanGaugePlaneFaceIndices[selectedPlaneIndex];
      for (const faceIndex of faceIndices) {
        const vertexIndex1 = indices.array[faceIndex * 3];
        const vertexIndex2 = indices.array[faceIndex * 3 + 1];
        const vertexIndex3 = indices.array[faceIndex * 3 + 2];
        colors1.array[vertexIndex1 * 3] = scanGaugeColor.r;
        colors1.array[vertexIndex1 * 3 + 1] = scanGaugeColor.g;
        colors1.array[vertexIndex1 * 3 + 2] = scanGaugeColor.b;
        colors1.array[vertexIndex2 * 3] = scanGaugeColor.r;
        colors1.array[vertexIndex2 * 3 + 1] = scanGaugeColor.g;
        colors1.array[vertexIndex2 * 3 + 2] = scanGaugeColor.b;
        colors1.array[vertexIndex3 * 3] = scanGaugeColor.r;
        colors1.array[vertexIndex3 * 3 + 1] = scanGaugeColor.g;
        colors1.array[vertexIndex3 * 3 + 2] = scanGaugeColor.b;
      }
    }

    for (const gauge of gauges) {
      for (const faceIndex of gauge.faceIndices) {
        const vertexIndex1 = indices.array[faceIndex * 3];
        const vertexIndex2 = indices.array[faceIndex * 3 + 1];
        const vertexIndex3 = indices.array[faceIndex * 3 + 2];
        colors2.array[vertexIndex1 * 3] = scanGaugeColor.r;
        colors2.array[vertexIndex1 * 3 + 1] = scanGaugeColor.g;
        colors2.array[vertexIndex1 * 3 + 2] = scanGaugeColor.b;
        colors2.array[vertexIndex2 * 3] = scanGaugeColor.r;
        colors2.array[vertexIndex2 * 3 + 1] = scanGaugeColor.g;
        colors2.array[vertexIndex2 * 3 + 2] = scanGaugeColor.b;
        colors2.array[vertexIndex3 * 3] = scanGaugeColor.r;
        colors2.array[vertexIndex3 * 3 + 1] = scanGaugeColor.g;
        colors2.array[vertexIndex3 * 3 + 2] = scanGaugeColor.b;
      }
    }

    if (narrowScanBodies && narrowScanBodies.length !== 0) {
      for (const selectedPlaneIndex of this
        .selectedPlaneIndicesForNarrowScanBodies) {
        const faceIndices =
          this.narrowScanBodyPlaneFaceIndices[selectedPlaneIndex];
        for (const faceIndex of faceIndices) {
          const vertexIndex1 = indices.array[faceIndex * 3];
          const vertexIndex2 = indices.array[faceIndex * 3 + 1];
          const vertexIndex3 = indices.array[faceIndex * 3 + 2];
          colors1.array[vertexIndex1 * 3] = narrowScanBodyColor.r;
          colors1.array[vertexIndex1 * 3 + 1] = narrowScanBodyColor.g;
          colors1.array[vertexIndex1 * 3 + 2] = narrowScanBodyColor.b;
          colors1.array[vertexIndex2 * 3] = narrowScanBodyColor.r;
          colors1.array[vertexIndex2 * 3 + 1] = narrowScanBodyColor.g;
          colors1.array[vertexIndex2 * 3 + 2] = narrowScanBodyColor.b;
          colors1.array[vertexIndex3 * 3] = narrowScanBodyColor.r;
          colors1.array[vertexIndex3 * 3 + 1] = narrowScanBodyColor.g;
          colors1.array[vertexIndex3 * 3 + 2] = narrowScanBodyColor.b;
        }
      }

      for (const narrowScanBody of narrowScanBodies) {
        for (const faceIndex of narrowScanBody.faceIndices) {
          const vertexIndex1 = indices.array[faceIndex * 3];
          const vertexIndex2 = indices.array[faceIndex * 3 + 1];
          const vertexIndex3 = indices.array[faceIndex * 3 + 2];
          colors2.array[vertexIndex1 * 3] = narrowScanBodyColor.r;
          colors2.array[vertexIndex1 * 3 + 1] = narrowScanBodyColor.g;
          colors2.array[vertexIndex1 * 3 + 2] = narrowScanBodyColor.b;
          colors2.array[vertexIndex2 * 3] = narrowScanBodyColor.r;
          colors2.array[vertexIndex2 * 3 + 1] = narrowScanBodyColor.g;
          colors2.array[vertexIndex2 * 3 + 2] = narrowScanBodyColor.b;
          colors2.array[vertexIndex3 * 3] = narrowScanBodyColor.r;
          colors2.array[vertexIndex3 * 3 + 1] = narrowScanBodyColor.g;
          colors2.array[vertexIndex3 * 3 + 2] = narrowScanBodyColor.b;
        }
      }
    }

    colors1.needsUpdate = true;
    colors2.needsUpdate = true;

    this.mesh.material = this.colorsMaterial;
    this.mesh.material.uniforms.alpha.value = 0;
    return new Promise((resolve) => {
      new TWEEN.Tween(this.mesh.material.uniforms.alpha)
        .to({ value: 1 }, 1000)
        .easing(TWEEN.Easing.Quartic.Out)
        .onComplete(() => resolve())
        .start();
    });
  }

  onMouseDown(event) {
    if (!this.enableSelection) {
      return;
    }
    this.dragging = true;
    this.startDragPos.x = event.clientX;
    this.startDragPos.y = event.clientY;
  }

  onMouseUp(event) {
    if (!this.enableSelection) {
      return;
    }
    this.dragging = false;
    this.endDragPos.x = event.clientX;
    this.endDragPos.y = event.clientY;
    const distx = Math.abs(this.endDragPos.x - this.startDragPos.x);
    const disty = Math.abs(this.endDragPos.y - this.startDragPos.y);
    if (distx <= dragThreshold && disty <= dragThreshold) {
      this.onSelection();
    }
  }

  onMouseMove(event) {
    if (!this.enableSelection) {
      return;
    }
    if (
      this.dragging &&
      this.startDragPos.x !== event.clientX &&
      this.startDragPos.y !== event.clientY
    ) {
      this.clearHighlight();
      return;
    }
    const elementRect = this.element.getBoundingClientRect();
    const mouseX = event.clientX - elementRect.left;
    const mouseY = event.clientY - elementRect.top;
    this.mouseCoordsNDC.x = (mouseX / elementRect.width) * 2 - 1;
    this.mouseCoordsNDC.y = -(mouseY / elementRect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouseCoordsNDC, this.camera);
    this.onHighlight(this.raycaster.intersectObject(this.mesh));
  }

  onMouseLeave() {
    if (!this.enableSelection) {
      return;
    }
    this.dragging = false;
    this.clearHighlight();
  }

  onHighlight(intersection) {
    if (intersection.length === 0) {
      this.highlightedPlaneIndex = -1;
      this.narrowScanBodyHighlightedPlaneIndex = -1;
      return;
    }

    const uniforms = this.scanBodySelectMaterial.uniforms;

    const intersectedScanGaugePlaneIndex =
      this.scanGaugeFaceIndexToPlaneIndex[intersection[0].faceIndex];
    const intersectedNarrowScanBodyPlaneIndex =
      this.narrowScanBodyFaceIndexToPlaneIndex[intersection[0].faceIndex];

    if (
      !this.selectingNarrowScanBodies &&
      intersectedScanGaugePlaneIndex !== -1
    ) {
      let canHighlight = true;
      if (intersectedNarrowScanBodyPlaneIndex !== -1) {
        const selectedPlaneIndex =
          this.selectedPlaneIndicesForNarrowScanBodies.indexOf(
            intersectedNarrowScanBodyPlaneIndex
          );
        canHighlight = selectedPlaneIndex === -1;
      }

      if (canHighlight) {
        this.highlightedPlaneIndex = intersectedScanGaugePlaneIndex;
        const selectedPlaneIndex =
          this.selectedPlaneIndicesForScanGauges.indexOf(
            this.highlightedPlaneIndex
          );
        if (selectedPlaneIndex !== -1) {
          uniforms.scanGaugeHighlightedPlaneIndex.value = -1;
        } else {
          uniforms.scanGaugeHighlightedPlaneIndex.value =
            this.highlightedPlaneIndex;
        }
      }
    }

    if (
      this.selectingNarrowScanBodies &&
      intersectedNarrowScanBodyPlaneIndex !== -1
    ) {
      let canHighlight = true;
      if (intersectedScanGaugePlaneIndex !== -1) {
        const selectedPlaneIndex =
          this.selectedPlaneIndicesForScanGauges.indexOf(
            intersectedScanGaugePlaneIndex
          );
        canHighlight = selectedPlaneIndex === -1;
      }

      if (canHighlight) {
        this.narrowScanBodyHighlightedPlaneIndex =
          intersectedNarrowScanBodyPlaneIndex;
        const selectedPlaneIndex =
          this.selectedPlaneIndicesForNarrowScanBodies.indexOf(
            this.narrowScanBodyHighlightedPlaneIndex
          );
        if (selectedPlaneIndex !== -1) {
          uniforms.narrowScanBodyHighlightedPlaneIndex.value = -1;
        } else {
          uniforms.narrowScanBodyHighlightedPlaneIndex.value =
            this.narrowScanBodyHighlightedPlaneIndex;
        }
      }
    }
  }

  onSelection() {
    const uniforms = this.scanBodySelectMaterial.uniforms;
    let selectedPlaneIndices = this.selectedPlaneIndicesForScanGauges;
    let highlightedPlaneIndex = this.highlightedPlaneIndex;
    let uniformSelectedPlaneIndices = uniforms.scanGaugeSelectedPlaneIndices;
    let uniformSelectedPlanesCount = uniforms.scanGaugeSelectedPlanesCount;
    let uniformHighlightedPlaneIndex = uniforms.scanGaugeHighlightedPlaneIndex;
    if (this.selectingNarrowScanBodies) {
      selectedPlaneIndices = this.selectedPlaneIndicesForNarrowScanBodies;
      highlightedPlaneIndex = this.narrowScanBodyHighlightedPlaneIndex;
      uniformSelectedPlaneIndices = uniforms.narrowScanBodySelectedPlaneIndices;
      uniformSelectedPlanesCount = uniforms.narrowScanBodySelectedPlanesCount;
      uniformHighlightedPlaneIndex =
        uniforms.narrowScanBodyHighlightedPlaneIndex;
    }

    if (highlightedPlaneIndex === -1) {
      return;
    }

    const index = selectedPlaneIndices.indexOf(highlightedPlaneIndex);
    if (index !== -1) {
      uniformSelectedPlaneIndices.value.splice(index, 1);
      uniformSelectedPlanesCount.value -= 1;
      selectedPlaneIndices.splice(index, 1);
      return;
    }

    uniformSelectedPlaneIndices.value[uniformSelectedPlanesCount.value] =
      highlightedPlaneIndex;
    uniformSelectedPlanesCount.value += 1;
    uniformHighlightedPlaneIndex.value = -1;
    selectedPlaneIndices.push(highlightedPlaneIndex);
  }

  clearHighlight() {
    const uniforms = this.scanBodySelectMaterial.uniforms;

    let uniformHighlightedPlaneIndex = uniforms.scanGaugeHighlightedPlaneIndex;
    if (this.selectingNarrowScanBodies) {
      uniformHighlightedPlaneIndex =
        uniforms.narrowScanBodyHighlightedPlaneIndex;
    }

    uniformHighlightedPlaneIndex.value = -1;
    this.highlightedPlaneIndex = -1;
    this.narrowScanBodyHighlightedPlaneIndex = -1;
  }
}
